import type { EnvironmentThemeId } from '../scene/themes.ts';
import type { RideAudioContext, RideAudioEventType } from './types.ts';

export type ProductionAudioAssetId =
  | 'music.builder-workshop'
  | 'ride.lift-loop'
  | 'ride.launch'
  | 'ride.brake'
  | 'ride.coasting-loop'
  | 'environment.park-model'
  | 'environment.toyworks'
  | 'environment.afterglow'
  | 'ride.photo'
  | 'ui.general-click'
  | 'ui.track-placement'
  | 'ui.build-selection'
  | 'ui.undo-delete'
  | 'ui.publish-success'
  | 'ui.coaster-complete'
  | 'ui.system-failure';

export type ProductionAudioLoopRole =
  'music' | 'coasting' | 'lift' | 'launch' | 'brake' | 'environment';

export interface ProductionAudioAsset {
  readonly id: ProductionAudioAssetId;
  readonly url: string;
  readonly bus: 'music' | 'ride' | 'environment' | 'ui';
  readonly kind: 'oneshot' | 'loop';
  readonly baseGain: number;
  readonly contexts: readonly RideAudioContext[];
  readonly event?: RideAudioEventType;
  readonly loopRole?: ProductionAudioLoopRole;
  readonly loopStartSeconds?: number;
  readonly loopEndSeconds?: number;
  readonly fadeInSeconds?: number;
  readonly fadeOutSeconds?: number;
  readonly theme?: EnvironmentThemeId;
  readonly socialExportSafe: boolean;
}

const asset = (value: ProductionAudioAsset): ProductionAudioAsset => Object.freeze(value);
const productionAudioBaseUrl = import.meta.env?.BASE_URL ?? '/BunchaCoasters/';
const audioUrl = (filename: string): string =>
  `${productionAudioBaseUrl}assets/audio/phase-9/${filename}`;

export const PRODUCTION_AUDIO_CATALOG: readonly ProductionAudioAsset[] = Object.freeze([
  asset({
    id: 'music.builder-workshop',
    url: audioUrl('music-builder-workshop.49e58c777faf.mp3'),
    bus: 'music',
    kind: 'loop',
    loopRole: 'music',
    loopStartSeconds: 0,
    loopEndSeconds: 60,
    baseGain: 0.12,
    contexts: ['editor'],
    socialExportSafe: false,
  }),
  asset({
    id: 'ride.lift-loop',
    url: audioUrl('ride-lift-loop.f8a7efb13f14.mp3'),
    bus: 'ride',
    kind: 'loop',
    loopRole: 'lift',
    loopStartSeconds: 6.17,
    loopEndSeconds: 12.44,
    fadeInSeconds: 0.08,
    fadeOutSeconds: 0.65,
    baseGain: 0.2325,
    contexts: ['ride', 'replay'],
    socialExportSafe: false,
  }),
  asset({
    id: 'ride.launch',
    url: audioUrl('ride-launch.c7af969dfb43.mp3'),
    bus: 'ride',
    kind: 'loop',
    loopRole: 'launch',
    loopStartSeconds: 0,
    loopEndSeconds: 21.03,
    baseGain: 0.21,
    fadeInSeconds: 0.45,
    fadeOutSeconds: 0.55,
    contexts: ['ride', 'replay'],
    socialExportSafe: false,
  }),
  asset({
    id: 'ride.brake',
    url: audioUrl('ride-brake.91e97b959a7d.mp3'),
    bus: 'ride',
    kind: 'loop',
    loopRole: 'brake',
    loopStartSeconds: 0,
    loopEndSeconds: 7.4,
    baseGain: 0.78,
    fadeInSeconds: 0.35,
    fadeOutSeconds: 0.55,
    contexts: ['ride', 'replay'],
    socialExportSafe: false,
  }),
  asset({
    id: 'ride.coasting-loop',
    url: audioUrl('ride-coasting-loop.1d9fd44d5b55.mp3'),
    bus: 'ride',
    kind: 'loop',
    loopRole: 'coasting',
    loopStartSeconds: 0,
    loopEndSeconds: 15,
    baseGain: 0.65,
    contexts: ['ride', 'replay'],
    socialExportSafe: false,
  }),
  asset({
    id: 'environment.park-model',
    url: audioUrl('environment-park-model.ecdbcb46dae2.mp3'),
    bus: 'environment',
    kind: 'loop',
    loopRole: 'environment',
    theme: 'park_model',
    loopStartSeconds: 0,
    loopEndSeconds: 14,
    baseGain: 0.18,
    contexts: ['editor', 'ride', 'replay'],
    socialExportSafe: false,
  }),
  asset({
    id: 'environment.toyworks',
    url: audioUrl('environment-toyworks.85f2ccd8b8e8.mp3'),
    bus: 'environment',
    kind: 'loop',
    loopRole: 'environment',
    theme: 'toyworks',
    loopStartSeconds: 0,
    loopEndSeconds: 9.4,
    baseGain: 0.18,
    contexts: ['editor', 'ride', 'replay'],
    socialExportSafe: false,
  }),
  asset({
    id: 'environment.afterglow',
    url: audioUrl('environment-afterglow.b95be2a1b86e.mp3'),
    bus: 'environment',
    kind: 'loop',
    loopRole: 'environment',
    theme: 'afterglow',
    loopStartSeconds: 0,
    loopEndSeconds: 14,
    baseGain: 0.08,
    contexts: ['editor', 'ride', 'replay'],
    socialExportSafe: false,
  }),
  asset({
    id: 'ride.photo',
    url: audioUrl('ride-photo.8d8173ca66b6.mp3'),
    bus: 'ride',
    kind: 'oneshot',
    event: 'photo_marker',
    baseGain: 1.6,
    contexts: ['ride', 'replay'],
    socialExportSafe: false,
  }),
  asset({
    id: 'ui.general-click',
    url: audioUrl('ui-general-click.a76fb20cc5fa.mp3'),
    bus: 'ui',
    kind: 'oneshot',
    event: 'ui_general_click',
    baseGain: 2.2,
    contexts: ['inactive', 'editor', 'ride', 'replay', 'media'],
    socialExportSafe: false,
  }),
  asset({
    id: 'ui.track-placement',
    url: audioUrl('ui-track-placement.c7730b0f1919.mp3'),
    bus: 'ui',
    kind: 'oneshot',
    event: 'track_place_success',
    baseGain: 2.3,
    contexts: ['editor'],
    socialExportSafe: false,
  }),
  asset({
    id: 'ui.build-selection',
    url: audioUrl('ui-build-selection.92f7e5e1f312.mp3'),
    bus: 'ui',
    kind: 'oneshot',
    event: 'ui_build_select',
    baseGain: 2.2,
    contexts: ['editor'],
    socialExportSafe: false,
  }),
  asset({
    id: 'ui.undo-delete',
    url: audioUrl('ui-undo-delete.63e48b349564.mp3'),
    bus: 'ui',
    kind: 'oneshot',
    event: 'ui_undo_delete',
    baseGain: 2,
    contexts: ['editor'],
    socialExportSafe: false,
  }),
  asset({
    id: 'ui.publish-success',
    url: audioUrl('ui-publish-success.6d2529997e45.mp3'),
    bus: 'ui',
    kind: 'oneshot',
    event: 'ui_publish_success',
    baseGain: 1.8,
    contexts: ['editor', 'media'],
    socialExportSafe: false,
  }),
  asset({
    id: 'ui.coaster-complete',
    url: audioUrl('ui-coaster-complete.15919354c371.mp3'),
    bus: 'ride',
    kind: 'oneshot',
    event: 'ride_complete',
    baseGain: 1.8,
    contexts: ['ride', 'replay'],
    socialExportSafe: false,
  }),
  asset({
    id: 'ui.system-failure',
    url: audioUrl('ui-system-failure.5a49f38001fb.mp3'),
    bus: 'ui',
    kind: 'oneshot',
    event: 'ui_system_failure',
    baseGain: 1.8,
    contexts: ['inactive', 'editor', 'ride', 'replay', 'media'],
    socialExportSafe: false,
  }),
]);

export function productionAudioForEvent(
  event: RideAudioEventType,
): ProductionAudioAsset | undefined {
  return PRODUCTION_AUDIO_CATALOG.find((entry) => entry.event === event);
}

export function productionAudioLoop(
  role: ProductionAudioLoopRole,
  theme?: EnvironmentThemeId,
): ProductionAudioAsset | undefined {
  return PRODUCTION_AUDIO_CATALOG.find(
    (entry) => entry.loopRole === role && (role !== 'environment' || entry.theme === theme),
  );
}
